import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { LoadingStatusCode } from "../../lib/types/LoadingStatusCode";
import { catchApiError } from "../../lib/utils/catchApiError";
import {
  fetchPriceTemplatesRoute,
  createPriceImportRoute,
  checkPriceImportStatusRoute,
  checkActiveImportRoute,
  deletePriceImportRoute,
  getPriceValidationResultRoute,
  savePriceImportRoute,
} from "../../lib/routes/individualTariffs";
import { PriceTemplate, ImportPriceData, PriceValidationResult } from "../../lib/types";

interface InitialState {
  priceTemplates: PriceTemplate[];
  priceTemplatesLoading: LoadingStatusCode;
  priceValidationResult: PriceValidationResult[];
  priceValidationResultTotalElements: number;
  priceValidationResultLoading: LoadingStatusCode;
  priceValidationClosingResult: PriceValidationResult[];
  priceValidationClosingResultTotalElements: number;
  priceValidationClosingResultLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  priceTemplates: [],
  priceTemplatesLoading: "idle",
  priceValidationResult: [],
  priceValidationResultTotalElements: 0,
  priceValidationResultLoading: "idle",
  priceValidationClosingResult: [],
  priceValidationClosingResultTotalElements: 0,
  priceValidationClosingResultLoading: "idle",
};

// Получение списка шаблонов
export const fetchPriceTemplates = createAsyncThunk(
  "individualTariffs/fetchPriceTemplates",
  async () => {
    try {
      const response = axios.get(fetchPriceTemplatesRoute());
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка шаблонов. Попробуйте позже",
      });
    }
  }
);

// Создание процедуры импорта
export const createPriceImport = createAsyncThunk(
  "individualTariffs/createPriceImport",
  async (importPriceData: ImportPriceData) => {
    try {
      const response = axios.post(createPriceImportRoute(), importPriceData);
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка создания процедуры импорта. Попробуйте позже",
      });
    }
  }
);

// Проверка статуса процедуры импорта
export const checkPriceImportStatus = createAsyncThunk(
  "individualTariffs/checkPriceImportStatus",
  async (importId: number) => {
    try {
      const response = axios.get(checkPriceImportStatusRoute(importId));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка проверки статуса импорта.",
      });
    }
  }
);

// Проверка наличия активного импорта
export const checkActiveImport = createAsyncThunk(
  "individualTariffs/checkActiveImport",
  async (agreementId: number) => {
    try {
      const response = axios.get(checkActiveImportRoute(agreementId));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка поиска активного импорта.",
      });
    }
  }
);

// Отмена импорта
export const deletePriceImport = createAsyncThunk(
  "individualTariffs/deletePriceImport",
  async (importId: number) => {
    try {
      const response = axios.delete(deletePriceImportRoute(importId));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка отмены импорта.",
      });
    }
  }
);

// Получение результатов валидации
export const getPriceValidationResult = createAsyncThunk(
  "individualTariffs/getPriceValidationResult",
  async (searchParams: { importId: number; page: number; size: number }) => {
    try {
      const response = axios.get(
        getPriceValidationResultRoute({ ...searchParams, closedDestination: false })
      );
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка получения результатов валидации.",
      });
    }
  }
);

// Получение результатов валидации для закрываемых направлений
export const getPriceValidationClosingResult = createAsyncThunk(
  "individualTariffs/getPriceValidationClosingResult",
  async (searchParams: { importId: number; page: number; size: number }) => {
    try {
      const response = axios.get(
        getPriceValidationResultRoute({ ...searchParams, closedDestination: true })
      );
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка получения результатов валидации.",
      });
    }
  }
);

// Сохранение импорта
export const savePriceImport = createAsyncThunk(
  "individualTariffs/savePriceImport",
  async (importId: number) => {
    try {
      const response = axios.post(savePriceImportRoute(importId));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка сохранения данных.",
      });
    }
  }
);

export const individualTariffsSlice = createSlice({
  name: "individualTariffs",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPriceTemplates.pending, (state) => {
        state.priceTemplatesLoading = "pending";
      })
      .addCase(fetchPriceTemplates.rejected, (state) => {
        state.priceTemplatesLoading = "rejected";
        state.priceTemplates = [];
      })
      .addCase(fetchPriceTemplates.fulfilled, (state, { payload }) => {
        state.priceTemplatesLoading = "fulfilled";
        state.priceTemplates = payload;
      })
      .addCase(getPriceValidationResult.pending, (state) => {
        state.priceValidationResultLoading = "pending";
      })
      .addCase(getPriceValidationResult.rejected, (state) => {
        state.priceValidationResultLoading = "rejected";
        state.priceValidationResult = [];
        state.priceValidationResultTotalElements = 0;
      })
      .addCase(getPriceValidationResult.fulfilled, (state, { payload }) => {
        state.priceValidationResultLoading = "fulfilled";
        state.priceValidationResult = payload.content;
        state.priceValidationResultTotalElements = payload.totalElements;
      })
      .addCase(getPriceValidationClosingResult.pending, (state) => {
        state.priceValidationClosingResultLoading = "pending";
      })
      .addCase(getPriceValidationClosingResult.rejected, (state) => {
        state.priceValidationClosingResultLoading = "rejected";
        state.priceValidationClosingResult = [];
        state.priceValidationClosingResultTotalElements = 0;
      })
      .addCase(getPriceValidationClosingResult.fulfilled, (state, { payload }) => {
        state.priceValidationClosingResultLoading = "fulfilled";
        state.priceValidationClosingResult = payload.content;
        state.priceValidationClosingResultTotalElements = payload.totalElements;
      });
  },
});

export const { reducer: individualTariffsReducer } = individualTariffsSlice;
